/**
 * The event head's desk — one page per event a member has been put in charge
 * of. It carries exactly the two committee powers the job needs: log a race
 * result, and log volunteer hours, both for ANY member and both filed against
 * this event. Nothing else on the committee portal opens up.
 *
 * The page exists only while the event is open. `canLogForEvent` decides that,
 * the same predicate the API uses, so a head who keeps the tab open in another
 * window and returns after the committee closes the event is turned away here
 * rather than at a failed POST.
 */

import { useNavigate, useParams } from 'react-router-dom';
import {
  bibNo,
  canLogForEvent,
  formatLongDate,
  formatTime,
  KIND_LABEL,
  today,
} from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { ResultSheet, VolunteerHoursSheet } from '../../components/sheets/ClubSheets';
import { BackButton, Button, ButtonRow, Empty, Eyebrow, Stat, Stats, Table } from '../../components/ui';

export function LeadEventPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { db, meId } = useClubData();
  const { openSheet } = useUI();

  const event = db.events.find((e) => e.id === eventId);

  /* Not mine, gone, or closed since the tab was drawn. One message for all
     three: which it is, is not the member's problem to diagnose. */
  if (!canLogForEvent(event, meId)) {
    return (
      <>
        <BackButton onClick={() => navigate('/m/bib')}>Back to my bib</BackButton>
        <Empty title="Not yours to log">
          {event?.completedAt
            ? 'The committee has closed this event. Ask them to reopen it if something needs changing.'
            : 'You are not leading this event.'}
        </Empty>
      </>
    );
  }

  const results = db.results.filter((r) => r.eventId === event.id);
  const hours = db.volunteer.filter((v) => v.eventId === event.id);
  const totalHours = hours.reduce((sum, v) => sum + v.hours, 0);
  const nameOf = (memberId) => db.members.find((m) => m.id === memberId);
  const isPast = event.date <= today();

  return (
    <>
      <BackButton onClick={() => navigate('/m/bib')}>Back to my bib</BackButton>

      {/* Ink masthead: this is a job, not one of the member's own screens, and
          it should not read like the rest of the member portal. */}
      <div className="mb-3.5 overflow-hidden border border-line bg-[linear-gradient(120deg,#161C2C,#0B0F18)] p-5 text-white">
        <div className="font-market text-[9.5px] font-bold uppercase tracking-[0.2em] text-gold-500">
          You are leading · {KIND_LABEL[event.kind]}
        </div>
        <div className="mt-2 font-market text-[26px] font-extrabold leading-[1.05] tracking-[-0.03em]">
          {event.title}
        </div>
        <div className="mt-2 font-market text-[12px] leading-[1.7] text-white/60">
          {formatLongDate(event.date)} · {event.time}
          {event.distanceKm ? ` · ${event.distanceKm} km` : ''}
          <br />
          {event.venue}
        </div>
        <div className="mt-4 border-t border-white/10 pt-3 font-market text-[11.5px] leading-relaxed text-white/55">
          {isPast
            ? 'Record what everyone did. The committee closes this event when you are done, and it disappears from your menu.'
            : 'This event has not been run yet. You can record results and hours as soon as it has.'}
        </div>
      </div>

      <div className="mb-[18px]">
        <ButtonRow>
          <Button tone="d" onClick={() => openSheet(<ResultSheet db={db} event={event} />)}>
            + Race result
          </Button>
          <Button tone="v" onClick={() => openSheet(<VolunteerHoursSheet db={db} event={event} />)}>
            + Volunteer hours
          </Button>
        </ButtonRow>
      </div>

      <Stats>
        <Stat value={results.length} label="Results logged" />
        <Stat value={hours.length} label="Hours entries" />
        <Stat tone="good" numberTone="good" value={totalHours} label="Total hours" />
      </Stats>

      <Eyebrow>Race results · {results.length}</Eyebrow>
      {results.length ? (
        <Table
          head={
            <tr>
              <th>Bib</th>
              <th>Member</th>
              <th>Dist</th>
              <th>Time</th>
            </tr>
          }
        >
          {results.map((r) => {
            const m = nameOf(r.memberId);
            return (
              <tr key={r.id}>
                <td className="tabular-nums">{m ? bibNo(m.bib) : '—'}</td>
                <td className="font-market font-bold text-ink-700">{m?.name ?? 'Unknown'}</td>
                <td>{r.dist}</td>
                <td className="tabular-nums">{formatTime(r.timeS)}</td>
              </tr>
            );
          })}
        </Table>
      ) : (
        <Empty title="No results yet" padding={30}>
          Log the first finisher with the button above.
        </Empty>
      )}

      <Eyebrow>Volunteer hours · {totalHours}</Eyebrow>
      {hours.length ? (
        <Table
          head={
            <tr>
              <th>Bib</th>
              <th>Member</th>
              <th>Duty</th>
              <th>Hours</th>
            </tr>
          }
        >
          {hours.map((v) => {
            const m = nameOf(v.memberId);
            return (
              <tr key={v.id}>
                <td className="tabular-nums">{m ? bibNo(m.bib) : '—'}</td>
                <td className="font-market font-bold text-ink-700">{m?.name ?? 'Unknown'}</td>
                <td>{v.role}</td>
                <td className="tabular-nums">{v.hours}</td>
              </tr>
            );
          })}
        </Table>
      ) : (
        <Empty title="No hours logged yet" padding={30}>
          Credit the people who helped run this event.
        </Empty>
      )}
    </>
  );
}
